import { Controller } from 'react-hook-form'
import { cn } from '@/lib/utils'
import Combobox from '@/components/ui/combo-box'

// Brands on Facebook Global Pages have many pages sharing one `name` ("JLL"), with the
// country/region qualifier only in `global_brand_page_name` ("JLL (AU)", "JLL (Default)").
// Prefer that label so those pages are actually distinguishable; fall back to `name` for
// pages outside a global structure, and for cached payloads fetched before we requested it.
export const pageLabel = (page) => page.global_brand_page_name || page.name || ''

// A Business Manager can see Pages the signed-in user has no role on. Those are annotated
// server-side rather than filtered out: a Page that silently vanishes from the list is
// undiagnosable, which is the support ticket this came from. `usable !== false` so payloads
// cached before the field existed keep working.
export const isPageUsable = (page) => page?.usable !== false

/**
 * Option rows for the picker, usable ones first and then alphabetically, so the Pages that
 * actually work are never buried under ones that can't be picked.
 */
export const buildPageOptions = (pages) =>
    [...(pages || [])]
        .sort((a, b) => {
            const byUsable = Number(isPageUsable(b)) - Number(isPageUsable(a))
            return byUsable !== 0 ? byUsable : pageLabel(a).localeCompare(pageLabel(b))
        })
        .map((page) => ({
            value: page.id,
            label: page.id + ' - ' + pageLabel(page),
            searchLabel: page.id + ' ' + pageLabel(page) + ' ' + (page.name || ''),
            displayLabel: page.id + ' - ' + pageLabel(page),
            disabled: !isPageUsable(page),
            hint: isPageUsable(page) ? null : page.unusable_reason,
        }))

const Selector = ({ field, errors, pages, value, onChange, disabled }) => {
    const options = buildPageOptions(pages)

    const selectedOption = options.find((option) => option.value === value)
    const selectedLabel = selectedOption?.displayLabel

    return (
        <Combobox
            {...field}
            value={value}
            options={options}
            onChange={onChange}
            disabled={disabled}
            selectedLabel={selectedLabel}
            className={cn(errors.facebookAccount && 'tw:!border-destructive')}
        />
    )
}

const FacebookPageSelector = ({ control, errors, pages, value, onChange, disabled }) => {
    return (
        <>
            <Controller
                name="facebookAccount"
                control={control}
                rules={{ required: 'Please select a Page' }}
                render={({ field }) => (
                    <Selector
                        field={field}
                        value={value}
                        errors={errors}
                        pages={pages}
                        onChange={onChange}
                        disabled={disabled}
                    />
                )}
            />
            {errors.facebookAccount && (
                <p className="tw:mt-1 tw:text-sm tw:text-error">{errors.facebookAccount.message}</p>
            )}
        </>
    )
}

export default FacebookPageSelector
